import toast from 'react-hot-toast';

/**
 * 消息到 toastId 的映射
 * 用于防止相同消息重复显示，相同消息会使用相同的 toastId，从而替换旧的 toast
 * 不同消息使用不同的 ID，可以同时显示
 */
const messageToIdMap = {
  success: new Map<string, string>(),
  error: new Map<string, string>(),
  loading: new Map<string, string>(),
};

/**
 * 生成基于消息的 toast ID
 * 相同消息会返回相同的 ID，从而替换旧的 toast，防止重复显示
 * 不同消息使用不同的 ID，可以同时显示多个 toast
 */
const getOrCreateToastId = (type: 'success' | 'error' | 'loading', message: string): string => {
  const map = messageToIdMap[type];
  if (!map.has(message)) {
    // 为新的消息创建唯一的 ID
    const id = `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    map.set(message, id);
  }
  return map.get(message)!;
};

/**
 * 防重复的 Toast 工具函数
 *
 * 工作原理：
 * - react-hot-toast 在使用相同 ID 时会自动更新 toast 内容，无需手动 dismiss
 * - 传了 id：使用该 id，相同 ID 的 toast 会自动更新（用于特定业务场景）
 * - 没传 id：根据消息内容自动分配 id，相同消息会更新，不同消息可以同时显示
 *
 * 关键修复：
 * - 移除了 toast.dismiss(id) + 立即创建新 toast 的方式
 * - 该方式会导致竞态条件：dismiss 的退出动画还未完成，新 toast 就创建了
 * - 导致新 toast 可能继承旧 toast 的"正在关闭"状态，从而永不消失
 * - react-hot-toast 会自动处理相同 ID 的更新，无需手动 dismiss
 */
export const toastUtils = {
  /**
   * 显示成功提示
   * - 传了 id：使用该 id，相同 ID 会自动更新内容
   * - 没传 id：根据消息内容分配 id，相同消息会更新，不同消息可以同时显示
   */
  success: (message: string, options?: { duration?: number; id?: string; }) => {
    const id = options?.id || getOrCreateToastId('success', message);
    // 确保消息不为空
    const safeMessage = message || 'Success';
    // react-hot-toast 会自动更新相同 ID 的 toast，无需手动 dismiss
    return toast.success(safeMessage, {
      id,
      duration: options?.duration || 2700,
    });
  },

  /**
   * 显示错误提示
   * - 传了 id：使用该 id，相同 ID 会自动更新内容
   * - 没传 id：根据消息内容分配 id，相同消息会更新，不同消息可以同时显示
   */
  error: (message: string, options?: { duration?: number; id?: string; }) => {
    const id = options?.id || getOrCreateToastId('error', message);
    // 确保消息不为空
    const safeMessage = message || 'An error occurred';
    // react-hot-toast 会自动更新相同 ID 的 toast，无需手动 dismiss
    return toast.error(safeMessage, {
      id,
      duration: options?.duration || 2700,
    });
  },

  /**
   * 显示加载提示
   * - 传了 id：使用该 id，相同 ID 会自动更新内容
   * - 没传 id：根据消息内容分配 id，相同消息会更新，不同消息可以同时显示
   */
  loading: (message: string, options?: { duration?: number; id?: string; }) => {
    const id = options?.id || getOrCreateToastId('loading', message);
    // 确保消息不为空
    const safeMessage = message || 'Loading...';
    // react-hot-toast 会自动更新相同 ID 的 toast，无需手动 dismiss
    return toast.loading(safeMessage, {
      id,
      duration: options?.duration || 2700,
    });
  },

  /**
   * 复制成功提示，使用固定 ID 防止重复
   */
  copySuccess: (message: string) => {
    return toast.success(message, {
      id: 'copy-success',
      duration: 2700,
    });
  },

  /**
   * 复制失败提示，使用固定 ID 防止重复
   */
  copyError: (message: string = 'Copy Failed') => {
    return toast.error(message, {
      id: 'copy-error',
      duration: 2700,
    });
  },

  /**
   * 清除所有 toast
   */
  dismissAll: () => {
    toast.dismiss();
  },

  /**
   * 清除指定 ID 的 toast
   */
  dismiss: (id: string) => {
    toast.dismiss(id);
  },
};
